import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Calendar, ChevronDown, LogOut, Lock, Loader2,
  Phone, Mail, Search, RefreshCw, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAdminAuth, adminHeaders } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "confirmed", "completed", "cancelled"];

const STATUS_META = {
  new:       { label: "New",       cls: "bg-[#D4B27E]/20 text-[#8B6D3F] border-[#D4B27E]/40" },
  confirmed: { label: "Confirmed", cls: "bg-[#2D4A3E]/10 text-[#2D4A3E] border-[#2D4A3E]/30" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function LoginScreen() {
  const { login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(password);
      toast.success("Welcome back");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="admin-login-screen">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#2D4A3E] text-white font-display font-bold">CS</span>
            <span className="font-display font-semibold text-lg text-[#1C221F]">City Smiles · Admin</span>
          </div>
          <p className="mt-3 text-sm text-[#57605B]">Sign in to view and manage appointments.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8 soft-shadow">
          <Label htmlFor="admin-password" className="text-sm">Admin password</Label>
          <div className="mt-1.5 relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57605B]" strokeWidth={1.75} />
            <Input
              id="admin-password"
              type="password"
              data-testid="input-admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="rounded-xl h-11 pl-10 bg-[#FAFAF9]"
              autoFocus
              required
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-rose-600">{error}</p>
          )}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="btn-admin-login"
            className="mt-5 w-full rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white h-11"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in…</>) : "Sign in"}
          </Button>
          <p className="mt-4 text-xs text-[#57605B] text-center">
            Forgot the password? Ask the clinic owner — it&apos;s stored in the site&apos;s configuration.
          </p>
        </form>

        <a href="/" className="mt-6 block text-center text-sm text-[#2D4A3E] hover:underline">← Back to website</a>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "default", testId }) {
  const tones = {
    default: "text-[#1C221F]",
    accent: "text-[#2D4A3E]",
    warn: "text-[#8B6D3F]",
    ok: "text-emerald-700",
  };
  return (
    <div data-testid={testId} className="bg-white border border-stone-200 rounded-2xl p-5 soft-shadow">
      <div className="text-xs uppercase tracking-widest text-[#57605B]">{label}</div>
      <div className={`mt-2 font-display text-3xl font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function AppointmentsDashboard() {
  const { token, logout } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/appointments`, { headers: adminHeaders(token) });
      setRows(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load appointments");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/appointments/${id}`,
        { status },
        { headers: adminHeaders(token) },
      );
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!query) return true;
      return (
        r.name.toLowerCase().includes(query) ||
        r.phone.toLowerCase().includes(query) ||
        r.service.toLowerCase().includes(query)
      );
    });
  }, [rows, filter, q]);

  const stats = useMemo(() => {
    const c = { total: rows.length, new: 0, confirmed: 0, completed: 0, cancelled: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="container-x flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2D4A3E] text-white font-display font-bold text-sm">CS</span>
            <span className="font-display font-semibold text-[#1C221F]">Admin · Appointments</span>
          </a>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={refreshing}
              data-testid="btn-refresh"
              className="rounded-full h-9 border-[#2D4A3E]/25 text-[#2D4A3E]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.75} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="btn-admin-logout"
              className="rounded-full h-9 text-[#57605B]"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard testId="stat-total"     label="Total"     value={stats.total} tone="accent" />
          <StatCard testId="stat-new"       label="New"       value={stats.new}   tone="warn" />
          <StatCard testId="stat-confirmed" label="Confirmed" value={stats.confirmed} tone="accent" />
          <StatCard testId="stat-completed" label="Completed" value={stats.completed} tone="ok" />
          <StatCard testId="stat-cancelled" label="Cancelled" value={stats.cancelled} />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57605B]" strokeWidth={1.75} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone, or service…"
              data-testid="input-search"
              className="pl-10 rounded-xl h-11 bg-white"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="select-filter" className="w-full sm:w-[180px] rounded-xl h-11 bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`filter-option-${s}`}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-stone-200 soft-shadow overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#57605B]">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              <div className="mt-3 text-sm">Loading appointments…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="empty-state" className="p-10 text-center text-[#57605B]">
              <div className="text-sm">No appointments match this view.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="appointments-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Booked</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} data-testid={`row-${r.id}`}>
                      <TableCell className="align-top">
                        <div className="font-medium text-[#1C221F]">{r.name}</div>
                        {r.message && (
                          <div className="mt-1 text-xs text-[#57605B] max-w-[240px] line-clamp-2">
                            &ldquo;{r.message}&rdquo;
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <a
                          href={`tel:${r.phone}`}
                          className="inline-flex items-center gap-1.5 text-sm text-[#2D4A3E] hover:underline"
                        >
                          <Phone className="w-3.5 h-3.5" strokeWidth={1.75} />
                          {r.phone}
                        </a>
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="mt-1 flex items-center gap-1.5 text-xs text-[#57605B] hover:underline"
                          >
                            <Mail className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {r.email}
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="align-top text-sm">{r.service}</TableCell>
                      <TableCell className="align-top text-sm">
                        <div className="flex items-center gap-1.5 text-[#1C221F]">
                          <Calendar className="w-3.5 h-3.5 text-[#57605B]" strokeWidth={1.75} />
                          {format(new Date(r.preferred_date), "d MMM yyyy")}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-[#57605B]">
                          <Clock className="w-3.5 h-3.5" strokeWidth={1.75} />
                          {r.preferred_time}
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-xs text-[#57605B]">
                        {format(new Date(r.created_at), "d MMM, HH:mm")}
                      </TableCell>
                      <TableCell className="align-top">
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger
                            data-testid={`select-status-${r.id}`}
                            className={`h-8 rounded-full text-xs font-medium border ${STATUS_META[r.status].cls} min-w-[130px]`}
                          >
                            <SelectValue />
                            <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s} data-testid={`opt-status-${s}-${r.id}`}>
                                <span className="inline-flex items-center gap-2">
                                  {s === "confirmed" && <CheckCircle2 className="w-3.5 h-3.5 text-[#2D4A3E]" strokeWidth={1.75} />}
                                  {s === "cancelled" && <XCircle className="w-3.5 h-3.5 text-rose-600" strokeWidth={1.75} />}
                                  {STATUS_META[s].label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-[#57605B]">
          Total {filtered.length} appointment{filtered.length === 1 ? "" : "s"} shown.
          Any changes save immediately.
        </p>
      </main>
    </div>
  );
}

export default function Admin() {
  const { token, loading } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <Loader2 className="w-6 h-6 animate-spin text-[#2D4A3E]" />
      </div>
    );
  }
  return token ? <AppointmentsDashboard /> : <LoginScreen />;
}

// small badge helper — kept but unused by main UI (available for future)
export function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return <Badge className={`rounded-full border ${meta.cls}`}>{meta.label}</Badge>;
}
